import { clientConfig } from "../config";
import { deviceIdForAccessToken } from "./device";
import type {
  Account,
  ConversationDescription,
  ConversationSummary,
  ConversationType,
  DirectoryUser,
  FriendRequests,
  MediaUpload,
  SocialContact,
  UserProfile,
  PersistedMessage,
  SendDraft,
} from "./types";

export class ApiError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: Readonly<Record<string, unknown>>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Typed browser façade for the public im-api Worker. */
export class ImApi {
  private readonly mediaUrls = new Map<string, string>();

  public constructor(private readonly token: () => Promise<string>) {}

  public async bootstrap(): Promise<Account> {
    const body = await this.json<{ account: Account }>("/v1/account/bootstrap", { method: "POST" });
    return body.account;
  }

  public async account(): Promise<Account> {
    return (await this.json<{ account: Account }>("/v1/account")).account;
  }

  public async conversations(): Promise<readonly ConversationSummary[]> {
    return (await this.json<{ conversations: ConversationSummary[] }>("/v1/conversations?limit=100"))
      .conversations;
  }

  public async conversation(convId: string): Promise<ConversationDescription> {
    const path = `/v1/conversations/${encodeURIComponent(convId)}`;
    return (await this.json<{ conversation: ConversationDescription }>(path)).conversation;
  }

  public async searchUsers(query = ""): Promise<readonly DirectoryUser[]> {
    const path = `/v1/users?q=${encodeURIComponent(query)}&limit=50`;
    return (await this.json<{ users: DirectoryUser[] }>(path)).users;
  }

  public async profile(userId: string): Promise<UserProfile> {
    return (await this.json<{ profile: UserProfile }>(`/v1/profiles/${encodeURIComponent(userId)}`)).profile;
  }

  public async updateProfile(patch: Partial<Pick<UserProfile,
    "displayName" | "avatarUrl" | "bio" | "gender" | "birthday" | "location" |
    "customFields" | "friendPolicy" | "messagePolicy"
  >>): Promise<UserProfile> {
    return (await this.json<{ profile: UserProfile }>("/v1/profile", {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    })).profile;
  }

  public async friends(): Promise<readonly SocialContact[]> {
    return (await this.json<{ friends: SocialContact[] }>("/v1/friends")).friends;
  }

  public async friendRequests(): Promise<FriendRequests> {
    return this.json<FriendRequests>("/v1/friend-requests");
  }

  public async requestFriend(userId: string, message?: string): Promise<void> {
    await this.json("/v1/friend-requests", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userId, ...(message === undefined ? {} : { message }) }),
    });
  }

  public async respondFriend(requesterId: string, action: "accept" | "decline"): Promise<void> {
    await this.json(`/v1/friend-requests/${encodeURIComponent(requesterId)}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ action }),
    });
  }

  public async updateFriend(
    userId: string,
    patch: { readonly remark?: string | null; readonly groups?: readonly string[] },
  ): Promise<void> {
    await this.json(`/v1/friends/${encodeURIComponent(userId)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    });
  }

  public async deleteFriend(userId: string): Promise<void> {
    await this.request(`/v1/friends/${encodeURIComponent(userId)}`, { method: "DELETE" });
  }

  public async blockedUsers(): Promise<readonly SocialContact[]> {
    return (await this.json<{ users: SocialContact[] }>("/v1/blocks")).users;
  }

  public async blockUser(userId: string): Promise<void> {
    await this.json("/v1/blocks", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userId }),
    });
  }

  public async unblockUser(userId: string): Promise<void> {
    await this.request(`/v1/blocks/${encodeURIComponent(userId)}`, { method: "DELETE" });
  }

  public async presence(userIds: readonly string[]): Promise<ReadonlyMap<string, "online" | "offline">> {
    const result = await this.json<{ users: { userId: string; status: "online" | "offline" }[] }>(
      "/v1/presence/query",
      { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ userIds }) },
    );
    return new Map(result.users.map((user) => [user.userId, user.status]));
  }

  public async updateConversationPreference(
    convId: string,
    patch: Partial<Pick<ConversationSummary,
      "pinned" | "muted" | "markedUnread" | "draft"
    >> & { readonly hidden?: boolean; readonly clearHistory?: boolean },
  ): Promise<void> {
    await this.json(`/v1/conversations/${encodeURIComponent(convId)}/preference`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    });
  }

  public async createConversation(input: {
    readonly type: ConversationType;
    readonly title?: string;
    readonly members: readonly { userId: string; role: "owner" | "admin" | "member"; pool: string }[];
  }): Promise<{ convId: string; type: ConversationType; title: string | null }> {
    return this.json("/v1/conversations", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
  }

  public async updateConversation(
    convId: string,
    patch: { readonly title?: string; readonly dissolve?: true },
  ): Promise<void> {
    await this.request(`/v1/conversations/${encodeURIComponent(convId)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    });
  }

  public async addConversationMember(
    convId: string,
    member: { readonly userId: string; readonly pool: string; readonly role?: "admin" | "member" },
  ): Promise<void> {
    await this.json(`/v1/conversations/${encodeURIComponent(convId)}/members`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(member),
    });
  }

  public async updateConversationMember(
    convId: string,
    userId: string,
    patch: { readonly role?: "owner" | "admin" | "member"; readonly muteUntil?: number | null },
  ): Promise<void> {
    const path = `/v1/conversations/${encodeURIComponent(convId)}/members/${encodeURIComponent(userId)}`;
    await this.json(path, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    });
  }

  public async removeConversationMember(convId: string, userId: string): Promise<void> {
    const path = `/v1/conversations/${encodeURIComponent(convId)}/members/${encodeURIComponent(userId)}`;
    await this.request(path, { method: "DELETE" });
  }

  /** Sends through the HTTP fallback used by server-rendered and background App clients. */
  public async sendMessage(
    convId: string,
    draft: Omit<SendDraft, "clientMsgId"> & { readonly clientMsgId?: string },
  ): Promise<PersistedMessage> {
    return (await this.json<{ message: PersistedMessage }>("/v1/messages", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ convId, ...draft }),
    })).message;
  }

  public async sync(convId: string, afterSeq: number): Promise<{
    messages: readonly PersistedMessage[];
    currentSeq: number;
    nextAfterSeq: number;
  }> {
    try {
      return await this.syncPage(convId, afterSeq);
    } catch (cause) {
      const recoveryAfterSeq = historyRecoveryAfterSeq(cause);
      if (recoveryAfterSeq === null || recoveryAfterSeq === afterSeq) throw cause;
      return this.syncPage(convId, recoveryAfterSeq);
    }
  }

  public async connectTicket(): Promise<{
    ticket: string;
    webSocketUrl: string;
  }> {
    return this.json("/v1/connect-ticket", { method: "POST" });
  }

  public async revokeSession(): Promise<void> {
    await this.request("/v1/session/revoke", { method: "POST" });
  }

  public async upload(
    file: Blob,
    kind: "image" | "audio" | "video" | "file",
    fileName: string,
    onProgress?: (percent: number) => void,
  ): Promise<MediaUpload> {
    const token = await this.token();
    const currentDeviceId = deviceIdForAccessToken(
      token,
      clientConfig.oidcClientId,
      window.localStorage,
    );
    return new Promise<MediaUpload>((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open("POST", new URL("/v1/media", clientConfig.apiBaseUrl));
      request.setRequestHeader("authorization", `Bearer ${token}`);
      request.setRequestHeader("x-im-device-id", currentDeviceId);
      request.setRequestHeader("content-type", file.type);
      request.setRequestHeader("x-file-name", encodeURIComponent(fileName).slice(0, 180));
      request.setRequestHeader("x-media-kind", kind);
      request.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress?.(Math.min(99, Math.round((event.loaded / event.total) * 100)));
        }
      });
      request.addEventListener("load", () => {
        const body = parseUploadResponse(request.responseText);
        if (request.status >= 200 && request.status < 300 && body.media !== undefined) {
          onProgress?.(100);
          resolve(body.media);
          return;
        }
        reject(new ApiError(
          body.error?.message ?? `上传失败 (${request.status})`,
          request.status,
          body.error?.code,
        ));
      });
      request.addEventListener("error", () => {
        reject(new ApiError("上传网络连接中断，请重试", 0, "UPLOAD_NETWORK_ERROR"));
      });
      request.addEventListener("abort", () => {
        reject(new ApiError("上传已取消", 0, "UPLOAD_ABORTED"));
      });
      onProgress?.(0);
      request.send(file);
    });
  }

  /** Resolves protected R2 content to a tab-local Blob URL for img/audio tags. */
  public async mediaUrl(objectKey: string): Promise<string> {
    const cached = this.mediaUrls.get(objectKey);
    if (cached !== undefined) return cached;
    const response = await this.request(`/v1/media?objectKey=${encodeURIComponent(objectKey)}`);
    const url = URL.createObjectURL(await response.blob());
    this.mediaUrls.set(objectKey, url);
    return url;
  }

  public dispose(): void {
    for (const url of this.mediaUrls.values()) URL.revokeObjectURL(url);
    this.mediaUrls.clear();
  }

  private async json<T>(path: string, init?: RequestInit): Promise<T> {
    return await (await this.request(path, init)).json() as T;
  }

  private async request(path: string, init: RequestInit = {}): Promise<Response> {
    const token = await this.token();
    const headers = new Headers(init.headers);
    headers.set("authorization", `Bearer ${token}`);
    headers.set("x-im-device-id", deviceIdForAccessToken(
      token,
      clientConfig.oidcClientId,
      window.localStorage,
    ));
    const response = await fetch(new URL(path, clientConfig.apiBaseUrl), { ...init, headers });
    if (response.ok) return response;
    let message = `请求失败 (${response.status})`;
    let code: string | undefined;
    let details: Readonly<Record<string, unknown>> | undefined;
    try {
      const body = await response.json() as {
        error?: {
          code?: string;
          message?: string;
          details?: Readonly<Record<string, unknown>>;
        };
      };
      message = body.error?.message ?? message;
      code = body.error?.code;
      details = body.error?.details;
    } catch {
      // The status code remains useful when an intermediary returns non-JSON.
    }
    throw new ApiError(message, response.status, code, details);
  }

  private async syncPage(convId: string, afterSeq: number): Promise<{
    messages: readonly PersistedMessage[];
    currentSeq: number;
    nextAfterSeq: number;
  }> {
    const path = `/v1/conversations/${encodeURIComponent(convId)}/sync?afterSeq=${afterSeq}&limit=200`;
    return this.json(path);
  }
}

/** Returns the exclusive recovery cursor supplied by the retention-aware API. */
function historyRecoveryAfterSeq(cause: unknown): number | null {
  if (!(cause instanceof ApiError) || cause.code !== "HISTORY_EXPIRED") return null;
  const earliestAvailableSeq = cause.details?.earliestAvailableSeq;
  if (typeof earliestAvailableSeq !== "number" || !Number.isSafeInteger(earliestAvailableSeq)) {
    return null;
  }
  return Math.max(0, earliestAvailableSeq - 1);
}

function parseUploadResponse(value: string): {
  readonly media?: MediaUpload;
  readonly error?: { readonly code?: string; readonly message?: string };
} {
  try {
    return JSON.parse(value) as ReturnType<typeof parseUploadResponse>;
  } catch {
    return {};
  }
}
